import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";

import {
  Expression,
  GridData,
  GridDataOptions,
  GridProps,
  Pager,
  Sorter,
} from "./schema";
import {
  getDataSource,
  getDictionaryMapper,
  getGridData,
  getPagination,
} from "./util";
import { Button, Checkbox, List, SearchBar } from "antd-mobile";

const { Item } = List;
const Brief = Item.Brief;
const CheckboxItem = Checkbox.CheckboxItem;

const DataList = forwardRef(
  (
    {
      url,
      pagination,
      descriptions,
      useLegacyAPI,
      expressions,
      sorter,
      onComplete,
      onClickRow,
      rowSelectionType,
      pageSize = 10,
      render,
      extra,
    }: GridProps & {
      render?: (item: unknown, index: number) => void;
      extra?: JSX.Element;
    },
    ref: any
  ) => {
    const searchFormRef = useRef<{ [key: string]: string }>({});
    const [innerPagination, setInnerPagination] = useState<any>({});
    const [data, setData] = useState<any[]>([]);
    const [dictionary, setDictionary] = useState({});
    const [formattedData, setFormattedData] = useState<any[]>([]);
    const [pageIndex, setPageIndex] = useState<number>(1);
    const [total, setTotal] = useState(0);
    const [keyword, setKeyword] = useState<string>();

    /**
     * 仅查询数据
     * @param pager
     * @param currentSorter
     */
    const getData = useCallback(
      async function getData(
        pager?: Pager,
        currentSorter?: Sorter,
        keyword?: string
      ): Promise<GridData> {
        const options: GridDataOptions = {
          url,
          dictionaryMapper: getDictionaryMapper(descriptions),
          pagination: pagination !== false,
          pager: pager,
          sorter: currentSorter && currentSorter.order ? currentSorter : sorter,
          searchForm: searchFormRef.current,
          fuzzySearch: keyword,
          innerDescriptions: descriptions,
        };
        if (useLegacyAPI) {
          options.expressions = expressions || [
            new Expression("数量", "count(1)"),
          ];
        }
        return await getGridData(options);
      },
      [descriptions, expressions, pagination, sorter, url, useLegacyAPI]
    );

    /**
     * 加载数据
     * @param pager
     * @param currentSorter
     */
    const loadData = useCallback(
      async function loadData(
        pager?: Pager,
        currentSorter?: Sorter,
        searchKeyword?: string
      ) {
        const gridData = await getData(
          pager,
          currentSorter,
          searchKeyword ? searchKeyword : keyword
        );
        gridData &&
          gridData.data &&
          gridData.data.length > 0 &&
          gridData.data.forEach((item, index) => {
            if (!item.uuid) item.uuid = (+new Date()).toString() + index;
          });
        if (gridData) {
          if (useLegacyAPI) {
            setInnerPagination(
              getPagination(gridData, gridData.expressions!["数量"])
            );
          } else {
            setInnerPagination(getPagination(gridData, gridData.total || 10));
          }
          setData((prevState) => {
            if (pager?.pageIndex === 1) {
              return gridData.data;
            }
            return [...prevState, gridData.data];
          });
          setDictionary((prevState: { [key: string]: unknown[] }) => {
            if (pager?.pageIndex === 1) {
              return gridData.dictionary;
            }

            const result: { [key: string]: unknown[] } = {};
            for (const prop in prevState) {
              if (!Object.prototype.hasOwnProperty.call(prevState, prop)) {
                continue;
              }
              result[prop] = [...prevState[prop]];
              if (gridData.dictionary[prop]) {
                result[prop] = [...result[prop], ...gridData.dictionary[prop]];
              }
            }
            return result;
          });
          const currentFormattedData = getDataSource(gridData, descriptions);
          onComplete?.(currentFormattedData);
          gridData.data.forEach((element: any) => {
            delete element.children;
          });
          setFormattedData((prevState) => {
            if (pager?.pageIndex === 1) {
              return getDataSource(gridData, descriptions);
            }

            return [...prevState, ...getDataSource(gridData, descriptions)];
          });
        }
      },
      [descriptions, getData, onComplete, useLegacyAPI, keyword]
    );

    useEffect(() => {
      loadData({ pageIndex, pageSize: pageSize }).then();
    }, [loadData, pageIndex, pageSize]);

    // 选中行数据
    const [selectedRows, setSelectedRows] = useState<any[]>([]);
    useImperativeHandle(ref, () => ({
      getCheckedItem() {
        return selectedRows;
      },
      refresh(fn?: any) {
        loadData({
          pageIndex: pageIndex || 1,
          pageSize: pageSize || 10,
        }).then(() => {
          if (typeof fn === "function") {
            fn?.();
          }
        });
      },
    }));

    return (
      <>
        <List>
          <div className={"flex w-full items-center bg-[#efeff4]"}>
            <SearchBar
              className={"[&_.am-search-input]:rounded-full flex-1"}
              value={keyword}
              placeholder="Search"
              maxLength={8}
              onChange={(value) => {
                setPageIndex(1);
                setKeyword(value);
                //loadData({ pageIndex: 1, pageSize }, undefined, value).then();
              }}
            />
            <div>{extra}</div>
          </div>
          {rowSelectionType === "checkbox"
            ? formattedData.map((item, i) => (
                <CheckboxItem
                  style={{ marginLeft: 12 }}
                  key={item.uuid}
                  onChange={(e: { target: { checked: any } }) => {
                    const copy = JSON.parse(JSON.stringify(selectedRows));
                    if (e.target.checked) {
                      copy.push(item);
                    } else {
                      const index = copy.findIndex(
                        (a: any) => a.uuid === item.uuid
                      );
                      copy.splice(index, 1);
                    }
                    setSelectedRows(copy);
                  }}
                >
                  {
                    <Item wrap align="top" multipleLine>
                      {item[descriptions[0].fieldName!]}
                      <Brief>
                        {descriptions
                          .slice(1, descriptions.length)
                          .map((desc) => (
                            <div>{`${desc.fieldShowName}: ${
                              item[desc.fieldName!]
                            }`}</div>
                          ))}
                      </Brief>
                    </Item>
                  }
                </CheckboxItem>
              ))
            : formattedData.map((item, index) =>
                render ? (
                  render(item, index)
                ) : (
                  <Item
                    wrap
                    align="top"
                    multipleLine
                    onClick={() => {
                      if (onClickRow) {
                        onClickRow(item);
                      } else {
                        return;
                      }
                    }}
                  >
                    {item[descriptions[0].fieldName!]}
                    <Brief>
                      {descriptions
                        .slice(1, descriptions.length)
                        .filter((desc) => item[desc.fieldName!])
                        .map((desc) => (
                          <div className={"whitespace-normal"}>{`${
                            desc.fieldShowName
                          }: ${item[desc.fieldName!]}`}</div>
                        ))}
                    </Brief>
                  </Item>
                )
              )}
        </List>
        {formattedData.length < innerPagination.total && (
          <Button
            style={{ color: "#00b578" }}
            onClick={() => {
              setPageIndex((prevState) => prevState + 1);
            }}
          >
            加载更多
          </Button>
        )}
      </>
    );
  }
);

export default DataList;
